import { ErrorDetails } from "types/deviceAlarms.types";

// Asset imports for alarm icons
import ORPLowIcon from "assets/b_loworp.png";
import ORPHighIcon from "assets/b_highorp.png";
import PhLowIcon from "assets/b_lowph.png";
import PhHighIcon from "assets/b_highph.png";
import SalinityLowIcon from "assets/b_lowsalt.png";
import FlowLowIcon from "assets/b_lowflow.png";
import DaveyIcon from "assets/logo.png";

const alarmTypeOverrides: Record<number, string> = {
  1022: "CELL_ERROR",
};

export const getAlarmDisplayType = (
  errorId: number,
  fallbackType: string,
): string => alarmTypeOverrides[errorId] || fallbackType;

// Utility function to get the first troubleshooting step for an alarm
export const getFirstTroubleshootingStep = (errorId: number): string => {
  const errorDetails = getErrorDetails(errorId);
  return errorDetails.troubleshooting[0] || "No troubleshooting available";
};

// Error details mapping function
export const getErrorDetails = (errorId: number): ErrorDetails => {
  const errorMap: Record<number, ErrorDetails> = {
    1001: {
      type: "ORP_ERROR",
      imageName: "orpError.png",
      title: "High ORP",
      message:
        "ORP (Oxidation Reduction Potential) level is higher than normal operating range.",
      troubleshooting: [
        "Check chlorine level and reduce if necessary",
        "Test and adjust pH levels to optimal range",
        "Verify ORP probe calibration",
        "Check for over-chlorination",
        "Allow natural ORP reduction through bather load or sunlight",
      ],
    },
    1002: {
      type: "ORP_ERROR",
      imageName: "orpError.png",
      title: "Low ORP",
      message:
        "ORP (Oxidation Reduction Potential) level is lower than safe operating range.",
      troubleshooting: [
        "Increase chlorine generation or dosing",
        "Check and clean ORP probe",
        "Verify proper water circulation",
        "Test pH level and adjust if necessary",
        "Check for heavy bather load or contamination",
      ],
    },
    1003: {
      type: "PH_ERROR",
      imageName: "phError.png",
      title: "Low pH",
      message: "Water pH is critically low and acidic for safe swimming.",
      troubleshooting: [
        "Add pH increaser (sodium carbonate) immediately",
        "Check total alkalinity and adjust if necessary",
        "Ensure proper water circulation for chemical distribution",
        "Re-test pH after chemical treatment",
        "Verify pH probe calibration",
      ],
    },
    1004: {
      type: "PH_ERROR",
      imageName: "phError.png",
      title: "High pH",
      message:
        "Water pH is critically high and alkaline, requiring immediate attention.",
      troubleshooting: [
        "Add pH reducer (sodium bisulfate or muriatic acid) immediately",
        "Check total alkalinity levels",
        "Ensure proper water circulation for chemical distribution",
        "Re-test pH after 4-6 hours of circulation",
        "Verify pH probe calibration",
      ],
    },
    1005: {
      type: "PH_WARN",
      imageName: "phWarning.png",
      title: "High pH Warning",
      message: "Water pH is above optimal range but not critical.",
      troubleshooting: [
        "Add pH reducer to bring to optimal range",
        "Monitor pH levels closely",
        "Check total alkalinity",
        "Ensure proper water circulation",
        "Test again in a few hours",
      ],
    },
    1006: {
      type: "ORP_WARN",
      imageName: "orpWarning.png",
      title: "High ORP Warning",
      message: "ORP level is above optimal range but not critical.",
      troubleshooting: [
        "Monitor ORP levels closely",
        "Consider reducing chlorine output",
        "Check pH levels",
        "Allow natural ORP reduction",
        "Re-test in a few hours",
      ],
    },
    1007: {
      type: "ORP_WARN",
      imageName: "orpWarning.png",
      title: "Low ORP Warning",
      message: "ORP level is below optimal range but not critical.",
      troubleshooting: [
        "Increase chlorine generation slightly",
        "Monitor ORP levels closely",
        "Check pH levels and adjust if needed",
        "Ensure proper water circulation",
        "Re-test in a few hours",
      ],
    },
    1008: {
      type: "PH_WARN",
      imageName: "phWarning.png",
      title: "Low pH Warning",
      message: "Water pH is below optimal range but not critical.",
      troubleshooting: [
        "Add pH increaser to bring to optimal range",
        "Monitor pH levels closely",
        "Check total alkalinity",
        "Ensure proper water circulation",
        "Test again in a few hours",
      ],
    },
    1009: {
      type: "SALT_WARN",
      imageName: "salinityWarning.png",
      title: "Low Salt Warning",
      message: "Salt level is below optimal range for chlorinator operation.",
      troubleshooting: [
        "Add salt to bring level to recommended range",
        "Monitor salt levels after addition",
        "Allow time for salt dissolution",
        "Verify salt test accuracy",
        "Check chlorinator operation",
      ],
    },
    1010: {
      type: "SALT_ERROR",
      imageName: "salinityError.png",
      title: "Low Salt Cut-Out",
      message: "Salt level is critically low causing chlorinator shutdown.",
      troubleshooting: [
        "Add salt immediately to reach minimum operating level",
        "Check for salt loss due to dilution",
        "Verify no recent heavy rainfall or water addition",
        "Test salt level with accurate test kit",
        "Restart chlorinator after salt addition",
      ],
    },
    1011: {
      type: "SALT_WARN",
      imageName: "salinityWarning.png",
      title: "Low Salt Cut-Out Warning",
      message: "Salt level approaching cut-out threshold.",
      troubleshooting: [
        "Add salt before reaching cut-out level",
        "Monitor salt levels daily",
        "Check for water dilution sources",
        "Verify salt test accuracy",
        "Plan salt addition maintenance",
      ],
    },
    1012: {
      type: "FLOW_ERROR",
      imageName: "lowFlowIcon.png",
      title: "Low Flow Error",
      message:
        "Water flow rate has dropped to critical levels causing system error.",
      troubleshooting: [
        "Immediately check pump strainer basket for blockages",
        "Inspect filter cartridges and clean or replace if clogged",
        "Verify all suction valves are fully open",
        "Check for air leaks in suction lines",
        "Examine pump impeller for debris or damage",
      ],
    },
    1013: {
      type: "CLEAN_ERROR",
      imageName: "cleanError.png",
      title: "Cell Requires Cleaning",
      message:
        "Chlorinator cell has accumulated calcium buildup and requires cleaning.",
      troubleshooting: [
        "Turn off chlorinator and remove cell",
        "Soak cell in muriatic acid solution (4:1 water to acid ratio)",
        "Use soft brush to remove calcium deposits",
        "Rinse thoroughly with fresh water",
        "Reinstall cell and restart system",
      ],
    },
    1014: {
      type: "ERROR_CLEAN",
      imageName: "errorClean.png",
      title: "Cell Requires Replacement",
      message:
        "Chlorinator cell has reached end of life and requires replacement.",
      troubleshooting: [
        "Order replacement cell from manufacturer",
        "Document cell operating hours",
        "Remove old cell carefully",
        "Install new cell according to manufacturer instructions",
        "Reset cell life counter",
      ],
    },
    1015: {
      type: "ERROR_CALIBRATE",
      imageName: "calibratePHProbe.png",
      title: "Calibrate pH Probe",
      message: "pH probe requires calibration for accurate readings.",
      troubleshooting: [
        "Use fresh pH calibration solutions (7.0)",
        "Follow manufacturer calibration procedure",
        "Clean probe before calibration",
        "Store probe properly after calibration",
        "Document calibration date",
      ],
    },
    1016: {
      type: "ERROR_CALIBRATE",
      imageName: "calibratePHProbe.png",
      title: "Calibrate ORP Probe",
      message: "ORP probe requires calibration for accurate readings.",
      troubleshooting: [
        "Use fresh ORP calibration solution (typically 470mV)",
        "Follow manufacturer calibration procedure",
        "Clean probe before calibration",
        "Ensure proper probe storage",
        "Document calibration date",
      ],
    },
    1017: {
      type: "ERROR_CALIBRATE",
      imageName: "calibratePHProbe.png",
      title: "Calibrate Salt or Temp Probe",
      message:
        "Salt or temperature probe requires calibration for accurate readings.",
      troubleshooting: [
        "Use appropriate calibration solution for probe type",
        "Follow manufacturer calibration procedure",
        "Verify probe connections",
        "Clean probe contacts if necessary",
        "Document calibration date and readings",
      ],
    },
    1018: {
      type: "WIFI_ERROR",
      imageName: "disconnectedWiFi.png",
      title: "Disconnected from Wi-Fi",
      message: "Device has lost Wi-Fi connection and cannot communicate.",
      troubleshooting: [
        "Check Wi-Fi router power and operation",
        "Verify Wi-Fi password hasn't changed",
        "Check signal strength at device location",
        "Restart device Wi-Fi connection",
        "Contact IT support if network issues persist",
      ],
    },
    1019: {
      type: "WIFI_ERROR",
      imageName: "disconnectedWiFi.png",
      title: "Disconnected from Wi-Fi",
      message: "Device has lost Wi-Fi connection and cannot communicate.",
      troubleshooting: [
        "Check Wi-Fi router power and operation",
        "Verify Wi-Fi password hasn't changed",
        "Check signal strength at device location",
        "Restart device Wi-Fi connection",
        "Contact IT support if network issues persist",
      ],
    },
    1020: {
      type: "SOFTWARE_UPDATE",
      imageName: "softwareUpdate.png",
      title: "Software Update Available",
      message: "New software version available for device.",
      troubleshooting: [
        "Connect device to stable Wi-Fi network",
        "Ensure device has adequate power",
        "Start software update process",
        "Do not interrupt update process",
        "Verify update completion",
      ],
    },
    1021: {
      type: "SOFTWARE_UPDATE",
      imageName: "softwareUpdate.png",
      title: "Software Update Required",
      message: "Critical software update required for device operation.",
      troubleshooting: [
        "Connect device to stable Wi-Fi network immediately",
        "Ensure device has adequate power",
        "Start software update process",
        "Do not interrupt update process",
        "Contact support if update fails",
      ],
    },
    1022: {
      type: "CELL_ERROR",
      imageName: "salinityError.png",
      title: "Check Pool Chemistry",
      message: "Pool chemistry requires immediate attention.",
      troubleshooting: [
        "Test all chemical parameters immediately",
        "Check salt, pH, and ORP levels",
        "Adjust chemicals as needed",
        "Ensure proper water circulation",
        "Re-test after adjustments",
      ],
    },
  };

  return (
    errorMap[errorId] || {
      type: "Unknown Error",
      imageName: "default-error.png",
      title: "Unknown Error Code",
      message: "An unrecognized error has occurred.",
      troubleshooting: ["Contact technical support for assistance"],
    }
  );
};

// Icon mapping function
export const getNotificationDetailsIcon = (errorId: number): string => {
  const iconMap: Record<number, string> = {
    1001: ORPHighIcon, // ORP_ERROR - High Orp
    1002: ORPLowIcon, // ORP_ERROR - Low Orp
    1003: PhLowIcon, // PH_ERROR - Low pH
    1004: PhHighIcon, // PH_ERROR - High pH
    1005: PhHighIcon, // PH_WARN - High pH
    1006: ORPHighIcon, // ORP_WARN - High Orp
    1007: ORPLowIcon, // ORP_WARN - Low Orp
    1008: PhLowIcon, // PH_WARN - Low pH
    1009: SalinityLowIcon, // SALT_WARN - Low Salt
    1010: SalinityLowIcon, // SALT_ERROR - Low Salt Cut-Out
    1011: SalinityLowIcon, // SALT_WARN - Low Salt Cut-Out
    1012: FlowLowIcon, // FLOW_ERROR - Low Flow
    1013: DaveyIcon, // CLEAN_ERROR - Cell Requires Cleaning
    1014: DaveyIcon, // ERROR_CLEAN - Cell Requires Replacement
    1015: PhLowIcon, // ERROR_CALIBRATE - Calibrate pH Probe
    1016: ORPLowIcon, // ERROR_CALIBRATE - Calibrate ORP Probe
    1017: SalinityLowIcon, // ERROR_CALIBRATE - Calibrate Salt or Temp Probe
    1018: DaveyIcon, // WIFI_ERROR - Disconnected from Wi-Fi
    1019: DaveyIcon, // WIFI_ERROR - Disconnected from Wi-Fi
    1020: DaveyIcon, // SOFTWARE_UPDATE - Software Update Available
    1021: DaveyIcon, // SOFTWARE_UPDATE - Software Update Required
    1022: SalinityLowIcon, // SALT_ERROR - Check Pool Chemistry
  };

  return iconMap[errorId] || DaveyIcon;
};

// Utility function to determine severity
export const getAlarmSeverity = (
  type: string
): "error" | "warning" | "info" => {
  if (
    type.includes("ERROR") ||
    type === "FLOW_ERROR" ||
    type === "SALT_ERROR" ||
    type === "ORP_ERROR" ||
    type === "PH_ERROR" ||
    type === "CLEAN_ERROR" ||
    type === "ERROR_CLEAN" ||
    type === "ERROR_CALIBRATE" ||
    type === "CELL_ERROR" ||
    type === "WIFI_ERROR"
  ) {
    return "error";
  } else if (
    type.includes("WARN") ||
    type === "PH_WARN" ||
    type === "ORP_WARN" ||
    type === "SALT_WARN"
  ) {
    return "warning";
  } else if (type === "SOFTWARE_UPDATE") {
    return "info";
  } else {
    return "info";
  }
};
